import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addOrUpdateProfileForm } from '../profiles/profileSlice';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  updatePassword,
  signOut,
  sendEmailVerification
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  limit
} from 'firebase/firestore';
import { db, auth } from '../../firebase/firebase';

/* create an email hash for the slug, f.ex "john-smith-1547289902" */
const hashCode = (s) => s.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0);
const REMEMBER_LOGIN_DAYS = 30;
const REMEMBER_LOGIN_UNTIL_KEY = 'kykyRememberUntil';

const setRememberLoginDeadline = (remember) => {
  if (typeof localStorage === 'undefined') return;

  if (!remember) {
    localStorage.removeItem(REMEMBER_LOGIN_UNTIL_KEY);
    return;
  }

  const expiresAt = Date.now() + REMEMBER_LOGIN_DAYS * 24 * 60 * 60 * 1000;
  localStorage.setItem(REMEMBER_LOGIN_UNTIL_KEY, String(expiresAt));
};

const getAuthErrorMessage = (error) => error?.code || error?.message || String(error);

const applyAuthPersistence = async (remember) => {
  try {
    await setPersistence(
      auth,
      remember ? browserLocalPersistence : browserSessionPersistence
    );
  } catch (error) {
    console.warn('Unable to update auth persistence:', getAuthErrorMessage(error));
  }
};

const getFullName = (firstname, lastname) =>
  [firstname, lastname].filter(Boolean).join(' ').trim();

const getStoredDisplayName = (userData = {}, authUser = {}) =>
  userData.displayName ||
  getFullName(userData.firstname, userData.lastname) ||
  authUser.displayName ||
  userData.username ||
  authUser.email?.split('@')[0] ||
  '';

export const signUpEmailAndPassword = createAsyncThunk(
  'user/signUpEmailAndPassword',
  async (
    {
      firstname,
      lastname,
      username,
      dateOfBirth,
      email,
      address,
      languages,
      password,
      subscribe
    },
    { rejectWithValue }
  ) => {
    try {
      const userQuery = query(collection(db, 'slugs'), where('name', '==', username), limit(1));
      const userSnapshots = await getDocs(userQuery);
      const isUserExist = !userSnapshots.empty;
      if (isUserExist) {
        return rejectWithValue('Firebase: Error (auth/username-already-in-use).');
      }

      const res = await createUserWithEmailAndPassword(auth, email, password);
      const user = res.user;
      const displayName = getFullName(firstname, lastname) || username;
      const slug = username.replace(/\W+/g, '-').toLowerCase() + '-' + hashCode(email);
      const time = serverTimestamp();
      await setDoc(doc(db, 'users', user.uid, 'data', 'userdata'), {
        uid: user.uid,
        firstname: firstname || '',
        lastname: lastname || '',
        displayName,
        username: username,
        dateOfBirth: dateOfBirth,
        email: email,
        address: address || {},
        languages: languages || [],
        subscribe: subscribe || false,
        authProvider: 'local',
        userType: 'buyer',
        slug: slug,
        created: time,
        lastseen: time,
      });
      await updateProfile(user, {
        photoURL: '',
        displayName
      });
      await setDoc(doc(db, 'slugs', slug), {
        uid: user.uid,
        name: username,
        photoURL: ''
      });
      await sendEmailVerification(user);
      await signOut(auth);
      return { emailVerificationSent: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  'user/changePassword',
  async ({ email, old_password, new_password }, { rejectWithValue }) => {
    try {
      const res = await signInWithEmailAndPassword(auth, email, old_password);
      await updatePassword(res.user, new_password);
      return res.user;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const signInEmailAndPassword = createAsyncThunk(
  'user/signInEmailAndPassword',
  async ({ email, password, remember = false }, { rejectWithValue }) => {
    try {
      await applyAuthPersistence(remember);
      const res = await signInWithEmailAndPassword(auth, email, password);

      if (!res.user.emailVerified) {
        await signOut(auth);
        setRememberLoginDeadline(false);
        return rejectWithValue('Firebase: Error (auth/email-not-verified).');
      }

      setRememberLoginDeadline(remember);

      const docRef = doc(db, 'users', res.user.uid, 'data', 'userdata');
      const docSnap = await getDoc(docRef);
      const time = serverTimestamp();

      if (!docSnap.exists()) {
        const username = email.split('@')[0];
        const displayName = res.user.displayName || username;
        const slug = username.replace(/\W+/g, '-').toLowerCase() + '-' + hashCode(email);
        
        await setDoc(docRef, {
          uid: res.user.uid,
          username: username,
          displayName,
          email: email,
          authProvider: 'local',
          userType: 'buyer',
          slug: slug,
          created: time,
          lastseen: time,
          userVatNumber: '',
        });
        
        await setDoc(doc(db, 'slugs', slug), {
          uid: res.user.uid,
          name: username,
          photoURL: ''
        });
        
        return {
          ...res.user,
          displayName,
          username,
          authProvider: 'local',
          userType: 'buyer',
          slug: slug,
          created: time,
          lastseen: time
        };
      }
      
      await updateDoc(docRef, {
        lastseen: time
      });

      const userData = docSnap.data();
      
      return {
        ...res.user,
        displayName: getStoredDisplayName(userData, res.user),
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
        username: userData.username || '',
        authProvider: userData.authProvider,
        userType: userData.userType,
        slug: userData.slug,
        created: userData.created,
        lastseen: userData.lastseen
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const recoverPasswordResetEmail = createAsyncThunk(
  'user/recoverPasswordResetEmail',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await sendPasswordResetEmail(auth, payload);
      return res;
    } catch (error) {
      return rejectWithValue(getAuthErrorMessage(error));
    }
  }
);

export const resendVerificationEmail = createAsyncThunk(
  'user/resendVerificationEmail',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const res = await signInWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(res.user);
      await signOut(auth);
      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const signInGoogleAuthProvider = createAsyncThunk(
  'user/signInGoogleAuthProvider',
  async ({ remember = false } = {}, { rejectWithValue }) => {
    try {
      await applyAuthPersistence(remember);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      const res = await signInWithPopup(auth, provider);
      setRememberLoginDeadline(remember);
      const user = res.user;
      const googleProviderData = user.providerData?.find(p => p.providerId === 'google.com');
      const resolvedPhotoURL = user.photoURL || googleProviderData?.photoURL || '';
      const time = serverTimestamp();
      const docSnap = await getDoc(doc(db, 'users', user.uid, 'data', 'userdata'));

      if (docSnap.exists()) {
        const userData = docSnap.data();
        const displayName = getStoredDisplayName(userData, user);
        // Always update lastseen + photoURL on every Google login
        await updateDoc(doc(db, 'users', user.uid, 'data', 'userdata'), {
          lastseen: time,
          photoURL: resolvedPhotoURL,
          displayName
        });
        return {
          ...user,
          uid: user.uid,
          email: user.email || '',
          displayName,
          firstname: userData.firstname || '',
          lastname: userData.lastname || '',
          username: userData.username || '',
          photoURL: resolvedPhotoURL,
          slug: userData.slug,
          authProvider: userData.authProvider,
          userType: userData.userType,
          created: userData.created,
          lastseen: userData.lastseen
        };
      }

      const displayName = user.displayName || user.email?.split('@')[0] || 'user';
      const slug = `${displayName.replace(/\W+/g, '-').toLowerCase()}-${hashCode(user.email || user.uid)}`;
      const nameParts = displayName.trim().split(/\s+/);
      const firstname = nameParts[0] || '';
      const lastname = nameParts.slice(1).join(' ') || '';
      await setDoc(doc(db, 'users', user.uid, 'data', 'userdata'), {
        uid: user.uid,
        firstname,
        lastname,
        displayName,
        username: displayName,
        email: user.email,
        photoURL: user.photoURL || '',
        authProvider: 'google',
        userType: 'buyer',
        slug,
        created: time,
        lastseen: time,
        userVatNumber: '',
      });
      await setDoc(doc(db, 'slugs', slug), {
        uid: user.uid,
        name: displayName,
        photoURL: ''
      });
      return {
        ...res.user,
        uid: user.uid,
        email: user.email || '',
        displayName,
        photoURL: user.photoURL || '',
        authProvider: 'google',
        userType: 'buyer',
        slug
      };
    } catch (error) {
      return rejectWithValue(getAuthErrorMessage(error));
    }
  }
);
export const logOut = createAsyncThunk('user/logOut', async (_, { rejectWithValue }) => {
  try {
    setRememberLoginDeadline(false);
    const res = await signOut(auth);
    return res;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const updateLastseen = createAsyncThunk('user/updateLastseen', async (uid, { rejectWithValue }) => {
  try {
    await updateDoc(doc(db, 'users', uid, 'data', 'userdata'), {
      lastseen: serverTimestamp()
    });
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const changeUserType = createAsyncThunk('user/changeUserType', async ({ uid, userType }, { rejectWithValue }) => {
  try {
    const userTypeRef = doc(db, 'users', uid, 'data', 'userdata');
    await setDoc(userTypeRef, { userType: userType }, { merge: true });
    return { userType: userType };
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

const initialState = {};

// createAsyncThunk() generates automatically pending -, fulfilled - and rejected handling cases
export const userSlice = createSlice({
  name: 'user',
  initialState: initialState,
  reducers: {
    clearUser() {
      return { ...initialState };
    },
    setPhotoURL(state, action) {
      return { ...state, photoURL: action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUpEmailAndPassword.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(signInEmailAndPassword.fulfilled, (state, action) => {
        return (state = {
          ...state,
          ...action.payload
        });
      })
      .addCase(signInGoogleAuthProvider.fulfilled, (state, action) => {
        return (state = {
          ...state,
          ...action.payload
        });
      })
      .addCase(logOut.fulfilled, (state, action) => {
        return { ...initialState };
      })
      .addCase(changeUserType.fulfilled, (state, action) => {
        return (state = {
          ...state,
          userType: action.payload.userType
        });
      })
      .addCase(addOrUpdateProfileForm.fulfilled, (state, action) => {
        const updates = {};
        if (action.payload?.url) {
          updates.photoURL = action.payload.url;
        }
        if (action.payload?.firstname !== undefined) {
          updates.firstname = action.payload.firstname;
        }
        if (action.payload?.lastname !== undefined) {
          updates.lastname = action.payload.lastname;
        }

        const displayName = getFullName(
          updates.firstname ?? state.firstname,
          updates.lastname ?? state.lastname
        );
        if (displayName) {
          updates.displayName = displayName;
        }

        return Object.keys(updates).length ? { ...state, ...updates } : state;
      });
  }
});

export const { clearUser, setPhotoURL } = userSlice.actions;
export default userSlice.reducer;
